export type FileIconType = 'pdf' | 'image' | 'doc' | 'sheet' | 'email' | 'html' | 'generic';

/** File extension to icon type for consistent styling */
export function getFileIconType(fileName?: string | null): FileIconType {
  if (!fileName) return 'generic';
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (['pdf'].includes(ext)) return 'pdf';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
  if (['eml', 'msg', 'oft'].includes(ext)) return 'email';
  if (['html', 'htm', 'json'].includes(ext)) return 'html';
  if (['doc', 'docx', 'txt', 'rtf'].includes(ext)) return 'doc';
  if (['xls', 'xlsx', 'csv'].includes(ext)) return 'sheet';
  return 'generic';
}

/**
 * Human-readable size for users: always KB or MB (or GB for very large files).
 * Avoids raw byte counts which are hard to scan.
 */
export function formatFileSize(bytes?: number | null): string {
  if (bytes == null || Number.isNaN(bytes)) return '—';
  const value = Math.max(0, bytes);
  if (value === 0) return '0 KB';

  const kb = value / 1024;
  const mb = value / (1024 * 1024);
  const gb = value / (1024 * 1024 * 1024);

  if (gb >= 1) return `${trimSizeNumber(gb)} GB`;
  if (mb >= 1) return `${trimSizeNumber(mb)} MB`;
  // Anything under 1 MB → KB (including tiny files)
  if (kb < 0.1) return '< 0.1 KB';
  return `${trimSizeNumber(kb)} KB`;
}

function trimSizeNumber(n: number): string {
  if (n >= 100) return String(Math.round(n));
  if (n >= 10) return n.toFixed(1).replace(/\.0$/, '');
  return n.toFixed(1);
}

/**
 * Format a loaded/total pair with one shared unit so progress is easy to read
 * (e.g. "1.2 MB / 4.1 MB" instead of mixing KB and MB).
 */
export function formatFileSizeProgress(loaded?: number | null, total?: number | null): string {
  if (total == null || total <= 0) {
    return loaded != null && loaded > 0 ? formatFileSize(loaded) : '—';
  }
  const safeLoaded = Math.max(0, loaded ?? 0);
  const unit =
    total >= 1024 * 1024 * 1024 ? 'GB' : total >= 1024 * 1024 ? 'MB' : 'KB';
  const divisor =
    unit === 'GB' ? 1024 * 1024 * 1024 : unit === 'MB' ? 1024 * 1024 : 1024;
  return `${trimSizeNumber(safeLoaded / divisor)} / ${trimSizeNumber(total / divisor)} ${unit}`;
}
